import React from 'react'
import distanceInWords from 'date-fns/formatDistance'
import format from 'date-fns/format'
import { Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Sensor, ThingspaceSensor } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { getActivationDateColor, getActiveContractorSensor, getActiveProjectSectionSensor, getLastPingColor } from './sensorBillingStatsHelpers'

const ActivationDateCell = ({ value }: { value?: string | null }) => (
  <Typography variant="body2" color={getActivationDateColor(value)}>
    {value ? format(new Date(value), 'P') : '-'}
  </Typography>
)

const DateCell = ({ value }: { value?: string | null }) => <Typography variant="body2">{value ? format(new Date(value), 'P') : '-'}</Typography>

const LastPingCell = ({ value }: { value?: string | null }) => (
  <Typography variant="body2" color={getLastPingColor(value)}>
    {value ? distanceInWords(new Date(value), new Date()) : '-'}
  </Typography>
)

export namespace SensorBillingColumns {
  export const ActivationDate: GridColDef<Sensor> = {
    field: 'thingspaceSensor.activationDate',
    headerName: 'Last Activation Date',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.thingspaceSensor?.activationDate || null,
    renderCell: ({ value }) => <ActivationDateCell value={value} />
  }

  export const LastConnectionDate: GridColDef<Sensor> = {
    field: 'thingspaceSensor.lastConnectionDate',
    headerName: 'Last Connection Date',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.thingspaceSensor?.lastConnectionDate || null,
    renderCell: ({ value }) => <DateCell value={value} />
  }

  export const LastPing: GridColDef<Sensor> = {
    field: 'currentData.temperatureTime',
    headerName: 'Last Ping',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.currentData?.temperatureTime || null,
    valueFormatter: ({ value }) => (value ? distanceInWords(new Date(value), new Date()) : '-'),
    renderCell: ({ value }) => <LastPingCell value={value} />
  }
}

export namespace ThingspaceBillingColumns {
  export const Id: GridColDef<ThingspaceSensor> = {
    field: 'sensor.uniqueId',
    headerName: 'ID',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.sensor?.uniqueId || null,
    renderCell: ({ value }) => (value ? <InnerLink to={`/sensors/${value}`}>{value}</InnerLink> : '-')
  }

  export const IMEI: GridColDef<ThingspaceSensor> = {
    field: 'imei',
    headerName: 'IMEI',
    type: 'string',
    minWidth: 150,
    editable: false
  }

  export const DateAdded: GridColDef<ThingspaceSensor> = {
    field: 'sensor.createdAt',
    headerName: 'Date Added',
    type: 'string',
    minWidth: 110,
    editable: false,
    valueGetter: ({ row }) => row.sensor?.createdAt || null,
    valueFormatter: ({ value }) => (value ? format(new Date(value), 'P') : '-')
  }

  export const ActivationDate: GridColDef<ThingspaceSensor> = {
    field: 'activationDate',
    headerName: 'Last Activation Date',
    type: 'string',
    minWidth: 120,
    editable: false,
    renderCell: ({ value }) => <ActivationDateCell value={value} />
  }

  export const LastConnectionDate: GridColDef<ThingspaceSensor> = {
    field: 'lastConnectionDate',
    headerName: 'Last Connection Date',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.lastConnectionDate || null,
    renderCell: ({ value }) => <DateCell value={value} />
  }

  export const LastPing: GridColDef<ThingspaceSensor> = {
    field: 'sensor.currentData.temperatureTime',
    headerName: 'Last Ping',
    type: 'string',
    minWidth: 120,
    editable: false,
    valueGetter: ({ row }) => row.sensor?.currentData?.temperatureTime || null,
    valueFormatter: ({ value }) => (value ? distanceInWords(new Date(value), new Date()) : '-'),
    renderCell: ({ value }) => <LastPingCell value={value} />
  }

  export const Label: GridColDef<ThingspaceSensor> = {
    field: 'sensor.projectSectionsSensors.0.label',
    headerName: 'Label',
    type: 'string',
    minWidth: 110,
    editable: false,
    valueGetter: ({ row }) => (row.sensor?.projectSectionsSensors?.length && row.sensor.projectSectionsSensors[0].label) || null,
    valueFormatter: ({ value }) => value || '-'
  }

  export const Contractor: GridColDef<ThingspaceSensor> = {
    field: 'sensor.contractorSensors.0.contractor.name',
    headerName: 'Contractor',
    type: 'string',
    minWidth: 150,
    editable: false,
    valueGetter: ({ row }) => getActiveContractorSensor(row.sensor)?.contractor?.name || null,
    renderCell: ({ row }) => {
      const contractorSensor = getActiveContractorSensor(row.sensor)
      if (!contractorSensor) return '-'
      return <InnerLink to={`/contractors/${contractorSensor.contractorId}`}>{contractorSensor.contractor?.name}</InnerLink>
    }
  }

  export const Project: GridColDef<ThingspaceSensor> = {
    field: 'sensor.projectSectionsSensors.0.project.name',
    headerName: 'Project',
    type: 'string',
    minWidth: 150,
    editable: false,
    valueGetter: ({ row }) => getActiveProjectSectionSensor(row.sensor)?.project?.name || null,
    renderCell: ({ row }) => {
      const projectSectionSensor = getActiveProjectSectionSensor(row.sensor)
      if (!projectSectionSensor) return '-'
      return <InnerLink to={`/projects/${projectSectionSensor.projectId}`}>{projectSectionSensor.project?.name || '-'}</InnerLink>
    }
  }
}
